import React, { useMemo, useState } from 'react'
import { createRoot } from 'react-dom/client'

import { formatDate as formatDateValue } from '../utils/dateTime'
import { csrfToken } from '../utils/csrfToken'
import { formatMoney, MoneyInput } from './MoneyInput'

function formatDate(value) {
  if (!value) return '-'
  return formatDateValue(`${value}T00:00:00`) || value
}

function statusBadgeClass(status) {
  switch (status) {
    case 'ongoing':
      return 'badge text-bg-primary-subtle'
    case 'billed':
      return 'badge text-bg-info-subtle'
    case 'paid':
      return 'badge text-bg-success-subtle'
    case 'cancelled':
      return 'badge text-bg-danger-subtle'
    default:
      return 'badge text-bg-secondary-subtle'
  }
}

function renderStatusBadge(status) {
  const label = status ? status.toUpperCase() : '-'
  return <span className={statusBadgeClass(status)}>{label}</span>
}

function ProjectMilestonesTable({ initialMilestones, statuses, createUrl }) {
  const [milestones, setMilestones] = useState(initialMilestones)
  const [editingId, setEditingId] = useState(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState({
    name: '',
    target_start_date: '',
    target_end_date: '',
    status: statuses[0] || 'planned',
    price: '',
  })
  const [newDraft, setNewDraft] = useState({
    name: '',
    target_start_date: '',
    target_end_date: '',
    status: statuses[0] || 'planned',
    price: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const sortedMilestones = useMemo(() => {
    return [...milestones].sort((a, b) => {
      if (!a.target_start_date && !b.target_start_date) return 0
      if (!a.target_start_date) return 1
      if (!b.target_start_date) return -1
      return a.target_start_date.localeCompare(b.target_start_date)
    })
  }, [milestones])

  function beginEdit(milestone) {
    setEditingId(milestone.id)
    setDraft({
      name: milestone.name || '',
      target_start_date: milestone.target_start_date || '',
      target_end_date: milestone.target_end_date || '',
      status: milestone.status || 'planned',
      price: milestone.price ?? '',
    })
    setError(null)
  }

  function cancelEdit() {
    setEditingId(null)
    setError(null)
  }

  async function createMilestone() {
    if (!createUrl) return
    setSaving(true)
    setError(null)

    try {
      const response = await fetch(createUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken() || '',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ milestone: { ...newDraft, price: newDraft.price === '' ? 0 : newDraft.price } }),
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => null)
        throw new Error(payload?.errors ? 'Failed to create milestone.' : `Failed to create milestone (${response.status}).`)
      }

      const payload = await response.json()
      setMilestones((prev) => [...prev, payload])
      setNewDraft({
        name: '',
        target_start_date: '',
        target_end_date: '',
        status: statuses[0] || 'planned',
        price: '',
      })
      setCreating(false)
    } catch (err) {
      setError(err.message || 'Failed to create milestone.')
    } finally {
      setSaving(false)
    }
  }

  async function saveEdit(milestone) {
    setSaving(true)
    setError(null)

    try {
      const response = await fetch(milestone.update_url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken() || '',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ milestone: { ...draft } }),
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => null)
        throw new Error(payload?.errors ? 'Failed to save milestone.' : `Failed to save milestone (${response.status}).`)
      }

      const payload = await response.json()
      setMilestones((prev) => prev.map((item) => (item.id === milestone.id ? { ...item, ...payload } : item)))
      setEditingId(null)
    } catch (err) {
      setError(err.message || 'Failed to save milestone.')
    } finally {
      setSaving(false)
    }
  }

  async function deleteMilestone(milestone) {
    if (!window.confirm('Are you sure you want to delete this milestone?')) return

    try {
      const response = await fetch(milestone.delete_url, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken() || '',
        },
        credentials: 'same-origin',
      })

      if (!response.ok) throw new Error(`Failed to delete milestone (${response.status}).`)

      setMilestones((prev) => prev.filter((item) => item.id !== milestone.id))
      if (editingId === milestone.id) setEditingId(null)
    } catch (err) {
      setError(err.message || 'Failed to delete milestone.')
    }
  }

  return (
    <div>
      <div className="card-header d-flex align-items-center">
        <h4 className="card-header-title mb-0">Milestones</h4>
        <button
          type="button"
          className="btn btn-sm btn-primary ms-auto"
          onClick={() => setCreating(true)}
        >
          Add milestone
        </button>
      </div>
      {error ? <div className="alert alert-danger mb-0 mx-3 mt-3">{error}</div> : null}
      <div className="table-responsive mb-0">
        <table className="table table-sm table-nowrap card-table">
          <thead>
            <tr>
              <th>Milestone name</th>
              <th>Target start date</th>
              <th>Target end date</th>
              <th>Price</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {creating ? (
              <tr>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={newDraft.name}
                    onChange={(event) => setNewDraft({ ...newDraft, name: event.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={newDraft.target_start_date || ''}
                    onChange={(event) => setNewDraft({ ...newDraft, target_start_date: event.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={newDraft.target_end_date || ''}
                    onChange={(event) => setNewDraft({ ...newDraft, target_end_date: event.target.value })}
                  />
                </td>
                <td>
                  <MoneyInput
                    inputClassName="form-control form-control-sm"
                    value={newDraft.price}
                    onChange={(value) => setNewDraft({ ...newDraft, price: value })}
                    placeholder="PHP 0"
                  />
                </td>
                <td>
                  <select
                    className="form-select form-select-sm"
                    value={newDraft.status}
                    onChange={(event) => setNewDraft({ ...newDraft, status: event.target.value })}
                  >
                    {statuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="text-end">
                  <div className="btn-group btn-group-sm">
                    <button type="button" className="btn btn-primary" onClick={createMilestone} disabled={saving}>
                      Save
                    </button>
                    <button type="button" className="btn btn-white" onClick={() => setCreating(false)} disabled={saving}>
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
            ) : null}
            {sortedMilestones.length === 0 && !creating ? (
              <tr>
                <td colSpan="6" className="text-center text-muted py-4">No milestones found.</td>
              </tr>
            ) : null}
            {sortedMilestones.map((milestone) => (
              <tr key={milestone.id}>
                <td>
                  {editingId === milestone.id ? (
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={draft.name}
                      onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                    />
                  ) : (
                    milestone.name
                  )}
                </td>
                <td>
                  {editingId === milestone.id ? (
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={draft.target_start_date || ''}
                      onChange={(event) => setDraft({ ...draft, target_start_date: event.target.value })}
                    />
                  ) : (
                    formatDate(milestone.target_start_date)
                  )}
                </td>
                <td>
                  {editingId === milestone.id ? (
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={draft.target_end_date || ''}
                      onChange={(event) => setDraft({ ...draft, target_end_date: event.target.value })}
                    />
                  ) : (
                    formatDate(milestone.target_end_date)
                  )}
                </td>
                <td>
                  {editingId === milestone.id ? (
                    <MoneyInput
                      inputClassName="form-control form-control-sm"
                      value={draft.price}
                      onChange={(value) => setDraft({ ...draft, price: value })}
                      placeholder="PHP 0"
                    />
                  ) : (
                    formatMoney(milestone.price)
                  )}
                </td>
                <td>
                  {editingId === milestone.id ? (
                    <select
                      className="form-select form-select-sm"
                      value={draft.status}
                      onChange={(event) => setDraft({ ...draft, status: event.target.value })}
                    >
                      {statuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  ) : (
                    renderStatusBadge(milestone.status)
                  )}
                </td>
                <td className="text-end">
                  {editingId === milestone.id ? (
                    <div className="btn-group btn-group-sm">
                      <button type="button" className="btn btn-primary" onClick={() => saveEdit(milestone)} disabled={saving}>
                        Save
                      </button>
                      <button type="button" className="btn btn-white" onClick={cancelEdit} disabled={saving}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="btn-group btn-group-sm">
                      <button type="button" className="btn btn-white" onClick={() => beginEdit(milestone)}>
                        Edit
                      </button>
                      <button type="button" className="btn btn-danger" onClick={() => deleteMilestone(milestone)}>
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function mountProjectMilestonesTable(el) {
  if (!el) return
  const milestones = JSON.parse(el.dataset.milestones || '[]')
  const statuses = JSON.parse(el.dataset.milestoneStatuses || '[]')
  const createUrl = el.dataset.milestonesCreateUrl
  const root = createRoot(el)
  root.render(<ProjectMilestonesTable initialMilestones={milestones} statuses={statuses} createUrl={createUrl} />)
}
